import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import {
    ICheckpointAction,
    ICheckpointActionNotificationSettingsDialogData,
    ICheckpointActionAlertRecipient,
} from '@fi-shared/checklist/checklist-v2';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { cloneDeep } from '@fi-shared/utils';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { Subject } from 'rxjs';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';

@Component({
    selector: 'fi-checkpoint-action-task-editor-dialog',
    templateUrl: './action-task-editor-dialog.component.html',
    styleUrls: ['./action-task-editor-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-action-task-editor-dialog' },
    standalone: false,
})
export class FiCheckpointActionTaskEditorDialogComponent
    extends FiDialogsContentBase<ICheckpointActionNotificationSettingsDialogData, ICheckpointAction>
    implements OnInit, OnDestroy
{
    @ViewChild(FiValidatingFormDirective) taskEditorForm: FiValidatingFormDirective;

    protected readonly ObjectTypes = ObjectTypes;

    protected action: ICheckpointAction = cloneDeep(this.data.action);
    private fiValuesComparison: FiValuesComparison<ICheckpointAction>;

    public ngOnInit(): void {
        this.setServices();
        this.fiValuesComparison = new FiValuesComparison(() => this.action);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    protected onActionConditionChange(): void {
        this.updateValidity();
        this.cdr.markForCheck();
    }

    protected onRecipientChange(recipient: ICheckpointActionAlertRecipient): void {
        this.action.Settings.AlertRecipient = recipient;
        this.updateValidity();
    }

    protected updateValidity(): void {
        this.taskEditorForm.updateValidity();
    }

    public isYesButtonDisabled(): boolean {
        return !!this.taskEditorForm.errors || this.taskEditorForm.pending;
    }

    public getResult(): ICheckpointAction {
        return this.action;
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    protected get isTaskV2(): boolean {
        return this.fiTaskV2HelperService.getNormalizedTaskV2();
    }

    protected showTaskV2Fields(): boolean {
        return this.customerDataService.isTestCustomer();
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private cdr: ChangeDetectorRef;
    private fiDialogsService: FiDialogsService;
    private customerDataService: FiCustomerDataService;
    private fiTaskV2HelperService: FiTaskV2HelperService;

    private setServices(): void {
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.fiDialogsService = this.injector.get(FiDialogsService);
        this.customerDataService = this.injector.get(FiCustomerDataService);
        this.fiTaskV2HelperService = this.injector.get(FiTaskV2HelperService);
    }
}
